import {
  Popover,
  ListBox,
  EmptyState,
  FieldLabel,
  FieldError,
  inputFieldStyles,
  useRootContainer,
  POPOVER_LIST_BOX_MAX_HEIGHT,
} from "@appsmith/wds";
import React, { useMemo } from "react";
import { ComboBox as HeadlessCombobox } from "react-aria-components";

import styles from "./styles.module.css";
import type { ComboBoxProps } from "./types";
import { ComboBoxTrigger } from "./ComboBoxTrigger";

export const ComboBox = (props: ComboBoxProps) => {
  const {
    children,
    contextualHelp,
    emptyStateTitle,
    errorMessage,
    isDisabled,
    isLoading,
    isRequired,
    label,
    placeholder,
    size = "medium",
    ...rest
  } = props;
  const root = useRootContainer();

  // Empty state only when the caller provided copy (widgets own it) and
  // options are not loading (loading beats empty — M3 async contract).
  // Note: RAC only opens/keeps the popover open on an empty collection when
  // the caller also sets allowsEmptyCollection. Memoized so the ListBox does
  // not receive a fresh function every render (react-perf).
  const renderEmptyState = useMemo(() => {
    if (!Boolean(emptyStateTitle) || Boolean(isLoading)) return undefined;

    return function ListBoxEmptyState() {
      return <EmptyState title={emptyStateTitle as string} />;
    };
  }, [emptyStateTitle, isLoading]);

  return (
    <HeadlessCombobox
      aria-label={Boolean(label) ? undefined : "ComboBox"}
      className={inputFieldStyles.field}
      data-size={size}
      isDisabled={isDisabled}
      isRequired={isRequired}
      {...rest}
    >
      <FieldLabel
        contextualHelp={contextualHelp}
        isDisabled={isDisabled}
        isRequired={isRequired}
      >
        {label}
      </FieldLabel>
      <ComboBoxTrigger
        isDisabled={isDisabled}
        isLoading={isLoading}
        placeholder={placeholder}
        size={size}
      />
      <FieldError>{errorMessage}</FieldError>
      <Popover
        UNSTABLE_portalContainer={root}
        className={styles.comboboxPopover}
        maxHeight={POPOVER_LIST_BOX_MAX_HEIGHT}
      >
        <ListBox renderEmptyState={renderEmptyState} shouldFocusWrap>
          {children}
        </ListBox>
      </Popover>
    </HeadlessCombobox>
  );
};
